import { useCallback, useEffect } from "react";
import { twMerge } from "tailwind-merge";

import { MonitoringEventTypeType } from "../../shared/types/monitoring";
import { useAv, useAvPubsubMessaging } from "../../utils/av";

import { AnimatedBannerWrapper } from "./AnimatedBannerWrapper";
import { BaseFooter } from "./BaseFooter";
import { JoinInPersonClassButton } from "./JoinInPersonClassButton";
import LeaveClassButton from "./LeaveClassButton";
import { MobilePresentationBanner } from "./MobilePresentationBanner";
import { TimerBanner } from "./TimerBanner";
import { IconButtons } from "./icon-buttons/IconButtons";

import { withActiveAv } from "components/av/AvSubsystemLoader";
import { LargeClassHandControl } from "components/av/LargeClassHandControl";
import { ScreenshareOptions } from "components/av/ScreenshareOptions";
import { useVirtualClassActions } from "components/av/virtual-class-utils";
import { PillBanner } from "components/footer/PillBanner";
import { PillBannerButton } from "components/footer/PillBannerButton";
import { ReactionFeed } from "components/footer/ReactionFeed";
import { StudentPreviewOptions } from "components/footer/StudentPreviewOptions";
import { PresentationOptions } from "components/materials/presentation/PresentationOptions";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { VideoIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  selectIsClassSessionInPerson,
  selectIsPracticeClassSession,
  selectIsStudentPreview,
} from "store/selectors";
import { selectIsInBreakout } from "store/selectors/breakouts";
import {
  selectCurrentClassUser,
  selectCurrentClassUserIsInPersonClass,
} from "store/selectors/class-user";
import { selectScreensharingClassUserId } from "store/selectors/virtual-classes";
import { useCreateMonitoringEvent } from "utils/hooks/use-create-monitoring-event";
import { usePresentation } from "utils/presentation";

const LazyVideoMicControls = withActiveAv(() => import("components/av/VideoMicControls"));
const LazyBreakoutsBanner = withActiveAv(
  () => import("components/footer/breakouts-banner/BreakoutsBanner"),
);

export const StudentMainFooter = () => {
  const isStudentPreview = useAppSelector(selectIsStudentPreview);
  const screensharingClassUserId = useAppSelector(selectScreensharingClassUserId);
  const currentClassUser = useAppSelector(selectCurrentClassUser);
  const { presentedPageId } = usePresentation();
  const isBreakoutActive = useAppSelector(selectIsInBreakout);
  const isClassSessionInPerson = useAppSelector(selectIsClassSessionInPerson);
  const { isInVirtualClass, isAvActiveInAnotherTab, isVirtualClassActive, isAvSupportedInBrowser } =
    useAv();
  const isInInPersonClass = useAppSelector(selectCurrentClassUserIsInPersonClass);
  const isInClassSession = isInVirtualClass || isInInPersonClass;
  const isPracticeClassSession = useAppSelector(selectIsPracticeClassSession);
  const isInBreakoutRoom =
    isBreakoutActive && isInVirtualClass && !!currentClassUser?.breakout_group_id;
  const isMobile = !useIsMdOrLarger();
  const showPresentationOrScreenshareBanner =
    isInClassSession && (screensharingClassUserId || !isInBreakoutRoom);
  const isPresentationBannerVisible = !!(presentedPageId || screensharingClassUserId);

  const createMonitoringEvent = useCreateMonitoringEvent();
  useEffect(() => {
    if (isVirtualClassActive && isAvActiveInAnotherTab) {
      createMonitoringEvent(MonitoringEventTypeType.AV_PRESENT_IN_ANOTHER_TAB, {});
    }
  }, [isVirtualClassActive, isAvActiveInAnotherTab, createMonitoringEvent]);

  const { sendPubsubVirtualClassStateMessage } = useAvPubsubMessaging();

  const { showAvCheckToJoinVirtualClass } = useVirtualClassActions();

  const onJoinVirtualClass = useCallback(() => {
    showAvCheckToJoinVirtualClass();
  }, [showAvCheckToJoinVirtualClass]);

  const onMoveVirtualClassToThisTab = useCallback(() => {
    sendPubsubVirtualClassStateMessage({
      action: "join",
      force: true,
    });
  }, [sendPubsubVirtualClassStateMessage]);

  const MaybeJoinVirtualClassButton =
    isVirtualClassActive &&
    !isPracticeClassSession &&
    !isInVirtualClass &&
    isAvSupportedInBrowser ? (
      <PillBanner
        buttonChildren={
          isAvActiveInAnotherTab ? (
            <PillBannerButton
              eventName="Move Virtual Class to This Tab"
              kind="blue"
              title={t("av.move_virtual_class_to_this_tab")}
              onClick={onMoveVirtualClassToThisTab}
            >
              {t("av.move_virtual_class_to_this_tab")}
            </PillBannerButton>
          ) : (
            <PillBannerButton
              eventName="Enter class"
              kind="blue"
              title={t("footer.join_session")}
              onClick={onJoinVirtualClass}
            >
              {t("footer.join_session")}
            </PillBannerButton>
          )
        }
        kind="blue"
        textChildren={
          <IconText
            iconStart={<VideoIcon className="text-blue-shade-40" />}
            text={
              isAvActiveInAnotherTab
                ? t("av.virtual_class_in_another_tab")
                : t("av.virtual_class_in_session")
            }
            truncateText
          />
        }
      />
    ) : null;

  const MaybeJoinInPersonClassButton =
    isClassSessionInPerson && !isPracticeClassSession && !isInInPersonClass ? (
      <JoinInPersonClassButton />
    ) : null;

  return (
    <BaseFooter darkMode={isInClassSession} isMobile={isMobile}>
      {isInClassSession && <ReactionFeed />}
      <div
        className={twMerge(
          "flex h-full w-full items-center",
          isInClassSession && !isMobile && "justify-between",
          isInClassSession && isMobile && "justify-center",
          !isInClassSession && "justify-end",
        )}
      >
        <div className="flex items-center pr-2">
          <LazyVideoMicControls isMobile={isMobile} showOptionsMenu={!isMobile} />
          {!isMobile && <LargeClassHandControl />}
        </div>
        <div
          className={twMerge(
            "flex h-full min-w-0 items-center gap-2",
            !isInClassSession ? "justify-end" : "justify-center",
            !isMobile && "w-full",
          )}
        >
          {MaybeJoinVirtualClassButton}
          {MaybeJoinInPersonClassButton}
          {isVirtualClassActive && isInVirtualClass && isBreakoutActive && (
            <LazyBreakoutsBanner isMobile={isMobile} />
          )}
          {isInClassSession && <TimerBanner isMobile={isMobile} />}
          {showPresentationOrScreenshareBanner && isMobile && <MobilePresentationBanner />}

          {showPresentationOrScreenshareBanner && !isMobile && (
            <AnimatedBannerWrapper isVisible={isPresentationBannerVisible}>
              {screensharingClassUserId ? (
                <ScreenshareOptions autoFollow={isInClassSession} />
              ) : (
                <PresentationOptions isInClassSession />
              )}
            </AnimatedBannerWrapper>
          )}

          {isStudentPreview && <StudentPreviewOptions />}
          <IconButtons />
        </div>

        <div className="pl-2">{isInClassSession && <LeaveClassButton isMobile={isMobile} />}</div>
      </div>
    </BaseFooter>
  );
};
